// The builder's left panel (mockup 07): search, the constraint types in groups with their
// icons, "Show all", and under them a card for the chosen type (its variant, "all given" for
// dots, the switch for a whole-grid rule, the region being painted).
import type { PuzzleDoc } from '@zinc/shared';
import { globalIndex, negativeIndex, regionsOf } from '../../lib/builder/model';
import { GROUPS, PALETTE, searchPalette, type PaletteItem } from '../../lib/builder/palette';
import { SearchIcon } from '../icons';
import { ItemIcon } from './icons';

const HINTS: Record<string, string> = {
  line: 'Drag through the cells, diagonals too.',
  arrow: 'Drag from the circle along the shaft.',
  cage: 'Drag over the cells.',
  cell: 'Tap a cell; tap again to take it off.',
  edge: 'Tap the edge between two cells.',
  outside: 'Tap outside the grid, next to a row or column.',
  'little-killer': 'Tap outside the grid; tap again to turn it.',
  corner: 'Tap where four cells meet.',
  square: 'Tap the top-left cell of the 3×3 square.',
  clone: 'Drag one area, then its copy.',
  region: 'Pick a region, then drag over cells to move them into it.',
  global: 'A rule for the whole grid.',
};

/** Rules about every pair of a kind: "all dots given". */
const NEGATIVE: Record<string, { label: string; types: { type: string; values: number[] }[] }> = {
  kropki: {
    label: 'All kropki dots are given',
    types: [
      { type: 'difference', values: [1] },
      { type: 'ratio', values: [2] },
    ],
  },
  xv: {
    label: 'All X and V are given',
    types: [{ type: 'edge-sum', values: [5, 10] }],
  },
};

function Switch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex min-h-10 items-center gap-2 border-0 bg-transparent p-0 text-left text-[13px] text-ink"
    >
      <span className="flex-1">{label}</span>
      <span
        className={`box-border flex h-6 w-10 shrink-0 rounded-[12px] p-[3px] ${checked ? 'justify-end bg-accent' : 'justify-start bg-toggle-off'}`}
      >
        <span className="h-[18px] w-[18px] rounded-[9px] bg-surface shadow-[0_1px_2px_rgba(31,30,28,0.2)]" />
      </span>
    </button>
  );
}

export function OptionPills<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid gap-0.5 rounded-btn bg-segment p-[3px]"
      style={{ gridTemplateColumns: `repeat(${Math.min(options.length, 5)}, minmax(0, 1fr))` }}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={`h-8 rounded-[8px] border-0 text-[13px] ${on ? 'bg-surface font-semibold text-ink shadow-[0_1px_2px_rgba(31,30,28,0.14)]' : 'bg-transparent font-medium text-tool-ink'}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ItemButton({ item, on, onPick }: { item: PaletteItem; on: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      className={`flex h-[38px] min-w-0 items-center gap-2 rounded-[9px] border px-2 text-left text-[13px] leading-[1.15] ${on ? 'border-accent bg-accent-tint font-semibold text-accent-dark' : 'border-transparent bg-field font-medium text-ink'}`}
    >
      <ItemIcon id={item.id} />
      <span className="min-w-0">{item.name}</span>
    </button>
  );
}

export interface PaletteProps {
  doc: PuzzleDoc;
  query: string;
  onQuery: (q: string) => void;
  showAll: boolean;
  onShowAll: (all: boolean) => void;
  itemId: string | null;
  onItem: (id: string | null) => void;
  variant: string | null;
  onVariant: (key: string) => void;
  onGlobal: (type: string, params: Record<string, unknown> | undefined, on: boolean) => void;
  onNegative: (type: string, values: number[], on: boolean) => void;
  region: number;
  onRegion: (k: number) => void;
  regionProblem: string | null;
}

export function Palette(p: PaletteProps) {
  const items = searchPalette(p.query, p.showAll);
  const item = PALETTE.find((x) => x.id === p.itemId) ?? null;
  return (
    <div className="flex flex-col gap-3">
      <label className="flex h-10 items-center gap-2 rounded-btn border border-line bg-field px-2.5">
        <span className="flex text-muted">
          <SearchIcon size={16} />
        </span>
        <input
          type="search"
          placeholder="Search constraints"
          aria-label="Search constraints"
          value={p.query}
          onChange={(e) => p.onQuery(e.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent text-[14px] text-ink outline-none"
        />
      </label>
      {GROUPS.map((g) => {
        const inGroup = items.filter((x) => x.group === g.key);
        if (inGroup.length === 0) return null;
        return (
          <div key={g.key} className="flex flex-col gap-1.5">
            <h3 className="m-0 text-[11px] font-bold tracking-[0.06em] text-muted uppercase">
              {g.name}
            </h3>
            <div className="grid grid-cols-2 gap-1.5">
              {inGroup.map((x) => (
                <ItemButton
                  key={x.id}
                  item={x}
                  on={x.id === p.itemId}
                  onPick={() => p.onItem(x.id === p.itemId ? null : x.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
      {items.length === 0 && <p className="m-0 text-[13px] text-muted">No type matches.</p>}
      {!p.query && (
        <button
          type="button"
          onClick={() => p.onShowAll(!p.showAll)}
          className="self-start border-0 bg-transparent p-0 text-[13px] font-semibold text-accent"
        >
          {p.showAll ? 'Show fewer' : `Show all ${PALETTE.length} constraint types`}
        </button>
      )}
      {item && <ItemCard {...p} item={item} />}
    </div>
  );
}

function ItemCard(p: PaletteProps & { item: PaletteItem }) {
  const { item, doc } = p;
  const negative = NEGATIVE[item.id];
  const regionCount = regionsOf(doc).length || doc.grid.digits.length;
  return (
    <div
      aria-label={`${item.name} options`}
      className="flex flex-col gap-2 rounded-[12px] border border-line px-3 py-2.5"
    >
      <span className="text-[13px] font-semibold">{item.name}</span>
      <span className="text-[12px] text-muted">{HINTS[item.gesture]}</span>
      {item.gesture === 'global' && !item.variants && (
        <Switch
          label="On for this puzzle"
          checked={globalIndex(doc, item.type) >= 0}
          onChange={(on) => p.onGlobal(item.type, undefined, on)}
        />
      )}
      {item.gesture === 'global' &&
        item.variants?.map((v) => (
          <Switch
            key={v.key}
            label={`${item.name} ${v.label}`}
            checked={globalIndex(doc, v.type, v.params) >= 0}
            onChange={(on) => p.onGlobal(v.type, v.params, on)}
          />
        ))}
      {item.gesture !== 'global' && item.variants && (
        <OptionPills
          label={`${item.name} kind`}
          value={p.variant ?? item.variants[0]!.key}
          options={item.variants.map((v) => ({ value: v.key, label: v.label }))}
          onChange={p.onVariant}
        />
      )}
      {negative && (
        <Switch
          label={negative.label}
          checked={negative.types.every((t) => negativeIndex(doc, t.type) >= 0)}
          onChange={(on) => negative.types.forEach((t) => p.onNegative(t.type, t.values, on))}
        />
      )}
      {item.gesture === 'region' && (
        <>
          <div role="radiogroup" aria-label="Region" className="flex flex-wrap gap-1">
            {Array.from({ length: regionCount }, (_, k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={p.region === k}
                onClick={() => p.onRegion(k)}
                className={`h-8 w-8 rounded-[8px] border text-[13px] font-semibold ${p.region === k ? 'border-accent bg-accent-tint text-accent-dark' : 'border-line bg-surface text-ink'}`}
              >
                {k + 1}
              </button>
            ))}
          </div>
          {p.regionProblem && (
            <span role="status" className="text-[12px] text-mistake">
              {p.regionProblem}
            </span>
          )}
        </>
      )}
    </div>
  );
}
